import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import { Box, Typography, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { uploadCSV } from "../../../assets";

import * as Yup from "yup";
import { Form, Formik } from "formik";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import Button from "../../../component-lib/Molecules/button/Button";

interface ImportAdminUsersProps {
  handleOnSubmittedData?: (formData: { file: File | null }) => void;
  handleClose: () => void;
  isImporting?: boolean;
  importMessage?: string | null;
}

const ImportAdminUsers = ({
  handleOnSubmittedData,
  handleClose,
  isImporting = false,
  importMessage = null,
}: ImportAdminUsersProps) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const validationSchema = Yup.object().shape({});

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      setError("No file selected.");
      return;
    }

    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const allowedExtensions = [".csv", ".xls", ".xlsx"];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      setError("Only CSV, XLS, and XLSX files are allowed.");
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      const allowedTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      const allowedExtensions = [".csv", ".xls", ".xlsx"];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        setError("Only CSV, XLS, and XLSX files are allowed.");
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const downloadSampleExcel = () => {
    const sampleData = [
      [
        "bsaId",
        "firstName",
        "lastName",
        "email",
        "gender",
        "type",
        "contactNumber",
        "providerAccess",
      ],
      [
        "99001",
        "Heidi",
        "Morrow",
        "heidi.morrow@mailinator.com",
        "female",
        "Admin",
        "3982387329",
        "yes",
      ],
      [
        "99002",
        "Marcus",
        "Bennett",
        "marcus.bennett@mailinator.com",
        "male",
        "SuperAdmin",
        "4015558822",
        "no",
      ],
      [
        "99003",
        "Priya",
        "Nair",
        "priya.nair@mailinator.com",
        "female",
        "Admin",
        "",
        "",
      ],
      [
        "99004",
        "Leo",
        "Carter",
        "leo.carter@mailinator.com",
        "male",
        "SuperAdmin",
        "7705551234",
        "",
      ],
    ];

    const csvContent = sampleData.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "admin_users_sample.xlsx");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Formik
      initialValues={{
        file: null,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        try {
          const formData = { ...values, file: selectedFile };
          handleOnSubmittedData?.(formData);
        } catch (error) {
          console.log("Error in onSubmit:", error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <Form
            onSubmit={handleSubmit}
            className="h-full flex flex-col justify-between rounded-lg"
          >
            <div className="w-full flex flex-col gap-6 rounded-lg">
              <div>
                <div className="flex flex-col gap-4">
                  <Box textAlign="start">
                    <button
                      type="button"
                      className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors cursor-pointer w-full bg-transparent ${
                        isDragOver
                          ? "border-primary-main bg-primary-light"
                          : "border-neutral-30 hover:border-primary-main"
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() =>
                        document
                          .getElementById("admin-users-csv-upload")
                          ?.click()
                      }
                    >
                      <img
                        src={uploadCSV}
                        alt="Upload CSV"
                        className="mb-4 w-24 h-24"
                      />
                      <Typography
                        variant="h6"
                        className="text-primary-main underline cursor-pointer"
                      >
                        Upload File
                      </Typography>
                      <Typography
                        variant="body2"
                        className="text-neutral-60 mt-2"
                      >
                        Click to select or drag and drop your file here
                      </Typography>
                      <Typography
                        variant="caption"
                        className="text-neutral-50 mt-1"
                      >
                        Supported formats: CSV, XLS, XLSX
                      </Typography>
                    </button>
                    <input
                      type="file"
                      accept=".csv,.xls,.xlsx"
                      id="admin-users-csv-upload"
                      hidden
                      onChange={handleFileChange}
                    />

                    {error && (
                      <Typography color="error" className="mt-2">
                        {error}
                      </Typography>
                    )}

                    {importMessage && (
                      <Typography
                        color={
                          importMessage.includes("error") ||
                          importMessage.includes("Failed")
                            ? "error"
                            : "success"
                        }
                        className="mt-2"
                      >
                        {importMessage}
                      </Typography>
                    )}

                    {selectedFile && (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        className="mt-4 p-3 bg-neutral-10 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Typography variant="body2" className="font-medium">
                            {selectedFile.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            className="text-neutral-60"
                          >
                            ({(selectedFile.size / 1024).toFixed(1)} KB)
                          </Typography>
                        </div>
                        <IconButton
                          onClick={handleRemoveFile}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  <div className="bg-primary-light p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <Typography
                        variant="body2"
                        className=" text-neutral-63 mb-2"
                        style={{ fontSize: "14px" }}
                      >
                        File Format Requirements:
                      </Typography>
                      <Button
                        label="Download Sample File"
                        variant="outlined"
                        onClickCb={downloadSampleExcel}
                        style={{
                          minWidth: "140px",
                          height: "32px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          backgroundColor: "white",
                          border: "1px solid #005596",
                          color: "#005596",
                          fontFamily: "Inter",
                        }}
                      />
                    </div>
                    <ul className="text-sm text-neutral-60 space-y-1">
                      <li>
                        • File must be in CSV (.csv), XLS (.xls), or XLSX
                        (.xlsx) format
                      </li>
                      <li>
                        • Required headers: bsaId, firstName, lastName, email,
                        gender, type
                      </li>
                      <li>
                        • Optional headers: contactNumber, providerAccess
                      </li>
                      <li>• Maximum file size: 10MB</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-10 gap-4">
              <Button
                label={TEXT_CONSTANTS?.CANCEL}
                variant="outlined"
                onClickCb={() => {
                  handleClose();
                }}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: "white",
                  border: "1px solid #005596",
                  color: "#005596",
                  fontFamily: "Inter",
                }}
              />
              <Button
                disabled={!selectedFile || isSubmitting || isImporting}
                type="button"
                label={isImporting ? "Importing..." : "Import Admin Users"}
                variant="contained"
                onClickCb={() => {
                  if (!selectedFile) {
                    setError("Please select a file to import");
                    return;
                  }
                  handleSubmit();
                }}
                style={{
                  width: "220px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: isImporting ? "#ccc" : "#005596",
                  color: "#FFFFFF",
                  border: "1px solid #005596",
                  fontFamily: "Inter",
                }}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "auto",
    width: "708px",
    maxWidth: "none",
  },
}));

interface ImportAdminUsersDialogProps {
  isOpen: boolean;
  dialogHeading?: string;
  handleClose: () => void;
  handleImportAdminUsersData: (data: { file: File | null }) => void;
  isImporting?: boolean;
  importMessage?: string | null;
}

export default function ImportAdminUsersDialog({
  isOpen = false,
  handleClose,
  handleImportAdminUsersData,
  dialogHeading = "Import Admin Users",
  isImporting = false,
  importMessage = null,
}: Readonly<ImportAdminUsersDialogProps>) {
  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <div
        className="flex justify-between items-center rounded-lg"
        style={{ margin: "14px 24px" }}
      >
        <Heading title={dialogHeading} />
        <button
          onClick={handleClose}
          className="cursor-pointer border-none bg-transparent p-0"
          type="button"
          aria-label="Close dialog"
        >
          <CloseIcon />
        </button>
      </div>

      <DialogContent
        dividers
        style={{ padding: "16px 24px 24px 24px", borderRadius: "8px" }}
      >
        <ImportAdminUsers
          handleClose={handleClose}
          handleOnSubmittedData={(formData) => {
            handleImportAdminUsersData(formData);
          }}
          isImporting={isImporting}
          importMessage={importMessage}
        />
      </DialogContent>
    </BootstrapDialog>
  );
}
